import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { Genders } from '@chiroup/core/enums/Genders.enum';
import { Patient } from '@chiroup/core/types/Patient.type';
import { RequestStep } from '@chiroup/core/types/Request.type';
import { kioskSession } from './kioskSession';

// Kiosk service. Auth is a bearer-token session keyed off the device's
// per-install kiosk-id (stored in localStorage). The flow:
//   1) `pairInit` posts the kiosk-id; backend either issues a fresh
//      pair code (device unclaimed) or — once the clinic has claimed
//      it from the staff app — returns `paired: true` plus a session
//      token. We stash the token in `kioskSession`; main.tsx then
//      attaches it as `Authorization: Bearer <token>` on every
//      subsequent kiosk call.
//   2) `me` / `getRequest` / `createPendingPatient` / `forwardRequest`
//      are kiosk-scope authenticated; the wrapper resolves the kiosk
//      identity from the session row, no header juggling here.
//
// The legacy `x-kiosk-id` header has been retired. Existing kiosks
// that boot up with no stored session will hit a 401 on `me`,
// re-enter `pairInit`, get a token, and continue — no manual reset
// required.

const KIOSK_ID_KEY = 'kiosk-id';

export const getKioskId = (): string => {
  if (typeof window === 'undefined') return '';

  let id = window.localStorage.getItem(KIOSK_ID_KEY);

  if (!id) {
    id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `kiosk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(KIOSK_ID_KEY, id);
  }

  return id;
};

export const clearKioskId = () => {
  if (typeof window === 'undefined') return;

  window.localStorage.removeItem(KIOSK_ID_KEY);
  kioskSession.clear();
};

export type KioskClinicInfo = {
  clinicId: number;
  name: string;
  logo: string;
  primaryColor: string;
  accentColor: string;
  flags: string[];
  ehr?: boolean | number;
  country?: string;
  doNotAllowUnrelatedRegions?: boolean;
};

export type KioskPairInitResponse =
  | { paired: false; pairCode: string }
  | {
      paired: true;
      clinicId: number;
      location: number | null;
      // `token`/`expiresAt` are present after the clinic has claimed
      // the kiosk; we store them in `kioskSession` for the rest of
      // this device's session.
      token?: string;
      expiresAt?: string;
    };

export type KioskRequestBootstrap = {
  age: number | null;
  ccsFollowups?: string[];
  complaints?: any[];
  gender?: Genders;
  guardian?: boolean;
  guardianName?: string;
  language?: 'en' | 'es';
  methods?: any;
  patientName?: string;
  prMap?: { [key: string]: string };
  steps: RequestStep[];
  validationCode: string;
  smsConsent?: string;
  smsConsentMarketing?: string;
};

// Explicit Authorization header for every authed kiosk call. We
// can't rely on `main.tsx`'s fallback chain (`portal → request →
// kiosk`) because a stale portal session in this same browser would
// take precedence and the kiosk would send a patient-scoped token to
// `/kiosk/*` — wrapper rejects it with "Session not permitted for
// this endpoint." Per-call `headers` override the global callback.
const kioskAuthHeaders = (): { Authorization: string } | Record<string, never> => {
  const token = kioskSession.getToken();

  return token ? { Authorization: `Bearer ${token}` } : {};
};

const kioskService = () => {
  // Pair init is public — no token yet. We pass the kiosk-id in the
  // body; backend either issues a pair code or (post-claim) returns
  // a session token bound to this kiosk.
  const pairInit = async (kioskId: string): Promise<KioskPairInitResponse> => {
    const res: KioskPairInitResponse = await ChiroUpAPI.post('api-noauth', '/kiosk/pair', {
      body: {
        kioskId,
        operatingSystem:
          typeof navigator !== 'undefined' ? navigator.platform || 'unknown' : 'unknown',
        browser: typeof navigator !== 'undefined' ? navigator.userAgent || 'unknown' : 'unknown',
      },
    });

    if (res?.paired && res.token) {
      kioskSession.setToken(res.token, res.expiresAt);
    }

    return res;
  };

  // Read-only poll endpoint. The kiosk hits this every few seconds
  // after pair-init returns an unclaimed code; once the clinic claims
  // the device the response carries `paired: true` plus a fresh
  // kiosk-scoped session token, which we stash here so subsequent
  // calls auto-attach Bearer auth.
  const pairStatus = async (
    kioskId: string,
    pairCode: string
  ): Promise<
    | { paired: false }
    | {
        paired: true;
        clinicId: number;
        location: number | null;
        token?: string;
        expiresAt?: string;
      }
  > => {
    const res = await ChiroUpAPI.get('api-noauth', '/kiosk/pair/status', {
      queryParams: { kioskId, pairCode },
    });

    if (res?.paired && res.token) {
      kioskSession.setToken(res.token, res.expiresAt);
    }

    return res;
  };

  const me = async (_kioskId: string): Promise<KioskClinicInfo> => {
    return ChiroUpAPI.get('api', '/kiosk/me', { headers: kioskAuthHeaders() });
  };

  const getRequest = async ({
    phone,
    patientId,
  }: {
    kioskId?: string;
    phone?: string;
    patientId?: string;
  }): Promise<KioskRequestBootstrap | { patients: Patient[] }> => {
    const queryParams: Record<string, string> = {};

    if (phone) queryParams.phone = phone;
    if (patientId) queryParams.patientId = patientId;

    return ChiroUpAPI.get('api', '/kiosk/requests', {
      queryParams,
      headers: kioskAuthHeaders(),
    });
  };

  const createPendingPatient = async (
    _kioskId: string,
    patient: Partial<Patient>
  ): Promise<Patient> => {
    return ChiroUpAPI.post('api', '/kiosk/pending-patient', {
      body: patient,
      headers: kioskAuthHeaders(),
    });
  };

  const forwardRequest = async (
    _kioskId: string,
    args: { requestId: string; method: string; maskedMethod: string }
  ): Promise<any> => {
    return ChiroUpAPI.post('api', '/kiosk/forward-request', {
      body: args,
      headers: kioskAuthHeaders(),
    });
  };

  return {
    pairInit,
    pairStatus,
    me,
    getRequest,
    createPendingPatient,
    forwardRequest,
  };
};

export default kioskService();
